import React, { useCallback, useEffect, useState } from "react";
import Shell from "./components/Shell";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import ProfitLoss from "./pages/ProfitLoss";
import Equity from "./pages/Equity";
import Project from "./pages/Project";
import Expenses from "./pages/Expenses";
import Receivables from "./pages/Receivables";
import Unbilled from "./pages/Unbilled";
import Admin from "./pages/Admin";
import { useTheme } from "./theme";
import { useDensity } from "./density";
import {
  getToken, getUsername, getPages, saveSession, clearSession, setUnauthorizedHandler,
} from "./api";
import { clearAllUi } from "./lib/persist";

const TAB_KEY = "pd.tab";

const PAGES = {
  labour: Dashboard,
  pl: ProfitLoss,
  expenses: Expenses,
  receivables: Receivables,
  unbilled: Unbilled,
  equity: Equity,
  projects: Project,
  admin: Admin,
};

export default function App() {
  const { theme, toggle } = useTheme();
  const density = useDensity();
  const [token, setToken] = useState(() => getToken());
  const [username, setUsername] = useState(() => getUsername() || "");
  const [pages, setPages] = useState(() => getPages());
  // Where a session lands with nothing remembered: fd starts on Admin, which is
  // the work they are here to do; everyone else on the Dashboard.
  const [activeTab, setActiveTab] = useState(() =>
    localStorage.getItem(TAB_KEY)
      || ((getUsername() || "").toUpperCase() === "FD" ? "admin" : "labour")
  );

  const handleLogout = useCallback(() => {
    clearSession();
    clearAllUi();          // remembered filter selections are per-session
    setToken(null);
    setUsername("");
    setPages(null);
  }, []);

  // An expired or rejected token drops us back to the login screen rather than
  // leaving every panel stuck on an error.
  useEffect(() => {
    setUnauthorizedHandler(handleLogout);
  }, [handleLogout]);

  useEffect(() => {
    localStorage.setItem(TAB_KEY, activeTab);
  }, [activeTab]);

  const handleLogin = (newToken, user, userPages) => {
    saveSession(newToken, user, userPages);
    setToken(newToken);
    setUsername(user);
    setPages(userPages);
  };

  if (!token) return <Login onLogin={handleLogin} />;

  const isFD = (username || "").toUpperCase() === "FD";

  // Block non-fd users from admin tab
  if (activeTab === "admin" && !isFD) {
    setActiveTab("labour");
    return null;
  }

  // If user has page restrictions and current tab isn't allowed, redirect
  if (!isFD && pages && pages.length > 0 && !pages.includes(activeTab)) {
    setActiveTab(pages[0]);
    return null;
  }

  // A tab that has since been removed can still be sitting in localStorage or
  // in a stored page-access list, so fall back rather than render nothing.
  if (!PAGES[activeTab]) {
    setActiveTab(isFD ? "admin" : "labour");
    return null;
  }

  const Page = PAGES[activeTab];

  return (
    <Shell
      username={username}
      activeTab={activeTab}
      setActiveTab={setActiveTab}
      theme={theme}
      onToggleTheme={toggle}
      onLogout={handleLogout}
      density={density}
      pages={pages}
    >
      {/* Remounting on tab change keeps each page's fetch + filter state simple. */}
      <Page
        key={activeTab}
        username={username}
        colScale={density.colScale}
        density={density.density}
      />
    </Shell>
  );
}
